import React from "react";
import { api, formatPHP, formatPHTDate, formatPHTDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2,
  Pencil,
  Plus,
  Sun,
  Moon,
  Star,
  CalendarDays,
  RefreshCw,
} from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from "date-fns";
import AdminNewBookingDialog from "./AdminNewBookingDialog";
import AdminEditBookingDialog from "./AdminEditBookingDialog";

const TYPE_LABELS = {
  day_use: "Day",
  night_use: "Night",
  whole_day: "Overnight",
};
const TYPE_FULL = {
  day_use: "Day Use",
  night_use: "Night Use",
  whole_day: "Overnight Use",
};
const TYPE_ICON = {
  day_use: Sun,
  night_use: Moon,
  whole_day: Star,
};
const TYPE_PILL = {
  day_use: "bg-amber-400/15 text-amber-200 border-amber-400/40",
  night_use: "bg-indigo-400/15 text-indigo-200 border-indigo-400/40",
  whole_day: "bg-rose-400/15 text-rose-200 border-rose-400/40",
};

const STATUS_BADGE = {
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/40",
  on_hold: "bg-orange-500/20 text-orange-300 border-orange-500/40",
};
const STATUS_LABELS = {
  pending: "pending",
  confirmed: "confirmed",
  cancelled: "cancelled",
  on_hold: "on hold",
};

function holdExpiryLabel(iso) {
  if (!iso) return "";
  try {
    const exp = new Date(iso).getTime();
    const diffDays = Math.ceil((exp - Date.now()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "1 day left";
    return `${diffDays} days left`;
  } catch {
    return "";
  }
}

// Bucket bookings by every date they span (start..end inclusive). Skip cancelled.
function bucketByDate(bookings) {
  const map = {};
  for (const b of bookings) {
    if (b.status === "cancelled") continue;
    const start = b.booking_date;
    const end = b.end_date || start;
    if (!start) continue;
    let cur = new Date(start + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    while (cur <= last) {
      const k = format(cur, "yyyy-MM-dd");
      if (!map[k]) map[k] = [];
      map[k].push(b);
      cur = new Date(cur.getTime() + 86400000);
    }
  }
  return map;
}

export default function AdminBookings() {
  const [bookings, setBookings] = React.useState([]);
  const [rates, setRates] = React.useState([]);
  const [month, setMonth] = React.useState(new Date());
  const [loading, setLoading] = React.useState(true);
  const [selectedDate, setSelectedDate] = React.useState(null); // ISO yyyy-MM-dd
  const [previewBooking, setPreviewBooking] = React.useState(null);
  const [proofBlobUrl, setProofBlobUrl] = React.useState(null);
  const [editBooking, setEditBooking] = React.useState(null);
  const [newBookingOpen, setNewBookingOpen] = React.useState(false);
  const [newBookingDate, setNewBookingDate] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);
  const [sheetsConfigured, setSheetsConfigured] = React.useState(false);
  const [calendarSyncing, setCalendarSyncing] = React.useState(false);
  const [calendarConfigured, setCalendarConfigured] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings");
      setBookings(r.data || []);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    api.get("/rates").then((r) => setRates(r.data || [])).catch(() => {});
    api
      .get("/admin/sheets/status")
      .then((r) => setSheetsConfigured(!!r.data?.configured))
      .catch(() => {});
    api
      .get("/admin/calendar/status")
      .then((r) => setCalendarConfigured(!!r.data?.configured))
      .catch(() => {});
  }, []);

  const syncSheets = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/sheets/sync");
      if (r.data?.ok) {
        toast.success(`Synced ${r.data.synced} bookings to Google Sheets`);
      } else {
        toast.error(r.data?.reason || "Sync failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncCalendar = async () => {
    setCalendarSyncing(true);
    try {
      const r = await api.post("/admin/calendar/sync");
      if (r.data?.ok) {
        const msg = `Calendar: ${r.data.synced} synced${r.data.deleted ? `, ${r.data.deleted} removed` : ""}${r.data.failed ? `, ${r.data.failed} failed` : ""}`;
        toast.success(msg);
        load();
      } else {
        toast.error(r.data?.reason || "Calendar sync failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calendar sync failed");
    } finally {
      setCalendarSyncing(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Deleted");
    load();
  };

  const openPreview = async (b) => {
    setPreviewBooking(b);
    setProofBlobUrl(null);
    if (b.payment_proof_path) {
      try {
        const r = await api.get(`/files/proof/${b.id}`, { responseType: "blob" });
        setProofBlobUrl(URL.createObjectURL(r.data));
      } catch (e) {
        // ignore
      }
    }
  };

  React.useEffect(() => () => proofBlobUrl && URL.revokeObjectURL(proofBlobUrl), [proofBlobUrl]);

  // Calendar grid math
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const byDate = React.useMemo(() => bucketByDate(bookings), [bookings]);

  // Stats
  const guestBookings = bookings.filter((b) => !b.is_owner_block);
  const stats = {
    total: guestBookings.length,
    pending: guestBookings.filter((b) => b.status === "pending").length,
    confirmed: guestBookings.filter((b) => b.status === "confirmed").length,
    onHold: guestBookings.filter((b) => b.status === "on_hold").length,
    revenue: guestBookings
      .filter((b) => b.status === "confirmed")
      .reduce((s, b) => s + (b.total_price || 0), 0),
    ownerBlocks: bookings.filter((b) => b.is_owner_block).length,
  };

  const selectedDayBookings = selectedDate ? byDate[selectedDate] || [] : [];
  const dayPanelOpen = !!selectedDate;

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="overline mb-2">Reservations · Philippine Time (PHT)</p>
          <h1 className="font-serif-display text-4xl flex items-center gap-3">
            <CalendarDays size={28} className="text-[#C5A059]" />
            Bookings
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {sheetsConfigured && (
            <Button
              type="button"
              variant="outline"
              onClick={syncSheets}
              disabled={syncing}
              className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-full"
              data-testid="sync-sheets-button"
              title="Re-sync all bookings to your Google Sheet"
            >
              <RefreshCw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync to Sheets"}
            </Button>
          )}
          {calendarConfigured && (
            <Button
              type="button"
              variant="outline"
              onClick={syncCalendar}
              disabled={calendarSyncing}
              className="bg-transparent border-blue-500/40 text-blue-300 hover:bg-blue-500/10 rounded-full"
              data-testid="sync-calendar-button"
              title="Re-sync all bookings to your Google Calendar"
            >
              <CalendarDays size={14} className={`mr-1.5 ${calendarSyncing ? "animate-spin" : ""}`} />
              {calendarSyncing ? "Syncing…" : "Sync to Calendar"}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => {
              setNewBookingDate(format(new Date(), "yyyy-MM-dd"));
              setNewBookingOpen(true);
            }}
            className="gold-btn rounded-full px-5"
            data-testid="admin-new-booking-button"
          >
            <Plus size={16} className="mr-1.5" /> New Booking
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Guest Bookings" value={stats.total} />
        <Stat label="Pending" value={stats.pending} />
        <Stat label="Confirmed" value={stats.confirmed} />
        <Stat label="On Hold" value={stats.onHold} />
        <Stat label="Confirmed Revenue" value={formatPHP(stats.revenue)} />
        <Stat label="Owner Blocks" value={stats.ownerBlocks} />
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="bg-transparent border-[var(--border)]"
            onClick={() => setMonth((m) => subMonths(m, 1))}
            data-testid="cal-prev-month"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="bg-transparent border-[var(--border)]"
            onClick={() => setMonth((m) => addMonths(m, 1))}
            data-testid="cal-next-month"
          >
            <ChevronRight size={16} />
          </Button>
          <h2 className="font-serif-display text-2xl ml-3" data-testid="cal-month-label">
            {format(month, "MMMM yyyy")}
          </h2>
        </div>
        <Button
          variant="outline"
          className="bg-transparent border-[var(--border)] text-xs"
          onClick={() => setMonth(new Date())}
          data-testid="cal-today"
        >
          Today
        </Button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap mb-3 text-[11px] text-[#A1A4B5]">
        <LegendDot color="bg-amber-400" label="Day Use" />
        <LegendDot color="bg-indigo-400" label="Night Use" />
        <LegendDot color="bg-rose-400" label="Overnight Use" />
        <span className="opacity-60">·</span>
        <span>Solid = Confirmed · Dashed = Pending · Orange = On Hold</span>
      </div>

      {/* Calendar grid */}
      <div className="border border-[var(--border)] rounded-2xl overflow-hidden bg-[#0E1018]">
        <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.2em] text-[#A1A4B5] border-b border-[var(--border)]">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-3 py-2.5 text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const iso = format(d, "yyyy-MM-dd");
            const inMonth = isSameMonth(d, month);
            const today = isToday(d);
            const items = byDate[iso] || [];
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelectedDate(iso)}
                className={`min-h-[110px] text-left border-r border-b border-[var(--border)] last:border-r-0 p-2 transition-colors hover:bg-[#13151D] ${
                  inMonth ? "" : "opacity-40"
                } ${today ? "ring-1 ring-[#C5A059] ring-inset" : ""}`}
                data-testid={`cal-cell-${iso}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-xs font-medium ${
                      today ? "text-[#C5A059]" : inMonth ? "text-white" : "text-[#A1A4B5]"
                    }`}
                  >
                    {format(d, "d")}
                  </span>
                  {items.length > 0 && (
                    <span className="text-[9px] text-[#A1A4B5]">{items.length}</span>
                  )}
                </div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((b, i) => (
                    <div
                      key={`${b.id}-${i}`}
                      className={`px-1.5 py-0.5 rounded text-[10px] truncate border ${
                        TYPE_PILL[b.booking_type] || "bg-white/5"
                      } ${
                        b.status === "pending" ? "border-dashed" : ""
                      } ${b.status === "on_hold" ? "border-orange-500/60 text-orange-300" : ""}`}
                      title={`${b.guest_name} · ${TYPE_FULL[b.booking_type] || b.booking_type}`}
                    >
                      <span className="font-medium">{TYPE_LABELS[b.booking_type] || b.booking_type}</span>
                      <span className="opacity-80"> · {b.guest_name}</span>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <div className="text-[9px] text-[#C5A059]">+{items.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <p className="text-center text-[#A1A4B5] py-4 text-sm">Loading bookings…</p>
      )}

      {/* Day detail panel */}
      <Dialog open={dayPanelOpen} onOpenChange={(v) => !v && setSelectedDate(null)}>
        <DialogContent
          className="bg-[#0A0B10] border-[var(--border)] max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="day-panel-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {selectedDate ? formatPHTDate(selectedDate) : ""}
            </DialogTitle>
            <DialogDescription className="text-[#A1A4B5] text-xs">
              {selectedDayBookings.length === 0
                ? "No bookings on this date."
                : `${selectedDayBookings.length} booking${selectedDayBookings.length > 1 ? "s" : ""} on this date.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {selectedDayBookings.map((b) => {
              const Icon = TYPE_ICON[b.booking_type] || Sun;
              return (
                <div
                  key={b.id}
                  className="border border-[var(--border)] rounded-xl p-4 bg-[#13151D]"
                  data-testid={`day-booking-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg border ${TYPE_PILL[b.booking_type]}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{b.guest_name}</span>
                          {b.is_owner_block && (
                            <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-[#C5A059]/40 bg-[#C5A059]/10 text-[#C5A059]">
                              ✦ Owner
                            </span>
                          )}
                          <Badge className={`${STATUS_BADGE[b.status]} border capitalize`}>
                            {STATUS_LABELS[b.status]}
                          </Badge>
                        </div>
                        <div className="text-xs text-[#A1A4B5] mt-1">
                          {TYPE_FULL[b.booking_type]} · {b.num_guests} guests · {formatPHP(b.total_price)}
                        </div>
                        {b.num_days > 1 && (
                          <div className="text-[10px] text-[#A1A4B5] uppercase tracking-[0.15em] mt-0.5">
                            {formatPHTDate(b.booking_date)} → {formatPHTDate(b.end_date)} · {b.num_days} days
                          </div>
                        )}
                        {b.guest_email && (
                          <div className="text-[11px] text-[#A1A4B5] mt-1">{b.guest_email}</div>
                        )}
                        {b.guest_phone && (
                          <div className="text-[11px] text-[#A1A4B5]">{b.guest_phone}</div>
                        )}
                        {b.status === "on_hold" && b.hold_expires_at && (
                          <div className="text-[10px] text-orange-400/80 mt-1">
                            Hold · {holdExpiryLabel(b.hold_expires_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 flex-wrap mt-3">
                    <Button size="sm" variant="outline" className="bg-transparent border-[var(--border)]" onClick={() => openPreview(b)} data-testid={`view-booking-${b.id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="bg-transparent border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10" onClick={() => { setSelectedDate(null); setEditBooking(b); }} data-testid={`edit-booking-${b.id}`}>
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    {b.status !== "confirmed" && (
                      <Button size="sm" className="gold-btn" onClick={() => updateStatus(b.id, "confirmed")} data-testid={`confirm-booking-${b.id}`}>
                        Confirm
                      </Button>
                    )}
                    {b.status !== "on_hold" && b.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="bg-transparent border-orange-500/40 text-orange-400 hover:bg-orange-500/10" onClick={() => { if (window.confirm("Place on hold for 1 month? Date will be freed up immediately.")) updateStatus(b.id, "on_hold"); }} data-testid={`hold-booking-${b.id}`}>
                        Hold
                      </Button>
                    )}
                    {b.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => updateStatus(b.id, "cancelled")} data-testid={`cancel-booking-${b.id}`}>
                        Cancel
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(b.id)} data-testid={`delete-booking-${b.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}

            <div className="pt-2">
              <Button
                type="button"
                onClick={() => {
                  setNewBookingDate(selectedDate);
                  setSelectedDate(null);
                  setNewBookingOpen(true);
                }}
                className="gold-btn rounded-full w-full"
                data-testid="day-add-booking"
              >
                <Plus size={14} className="mr-1.5" /> Add Booking on this Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Detail Preview Dialog */}
      <Dialog open={!!previewBooking} onOpenChange={(v) => !v && setPreviewBooking(null)}>
        <DialogContent className="bg-[#0A0B10] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Booking Detail</DialogTitle>
            <DialogDescription className="text-[#A1A4B5] text-xs">
              All times in Philippine Time (PHT · Asia/Manila).
            </DialogDescription>
          </DialogHeader>
          {previewBooking && (
            <div className="space-y-3 text-sm">
              <Row k="Guest" v={previewBooking.guest_name} />
              <Row k="Email" v={previewBooking.guest_email || "—"} />
              <Row k="Phone" v={previewBooking.guest_phone || "—"} />
              <Row k="Type" v={TYPE_FULL[previewBooking.booking_type]} />
              <Row k="Date" v={formatPHTDate(previewBooking.booking_date)} />
              {previewBooking.num_days > 1 && (
                <>
                  <Row k="End Date" v={formatPHTDate(previewBooking.end_date)} />
                  <Row k="Length" v={`${previewBooking.num_days} days`} />
                </>
              )}
              <Row k="Guests" v={previewBooking.num_guests} />
              <Row k="Total" v={formatPHP(previewBooking.total_price)} />
              {previewBooking.addons?.length > 0 && (
                <Row
                  k="Add-ons"
                  v={`${previewBooking.addons
                    .map((a) => `${a.name} ${formatPHP(a.amount)}`)
                    .join(" · ")} (paid at villa)`}
                />
              )}
              <Row k="Downpayment" v={formatPHP(previewBooking.downpayment_amount)} />
              <Row k="Balance" v={formatPHP((previewBooking.total_price || 0) - (previewBooking.downpayment_amount || 0))} />
              <Row k="Status" v={STATUS_LABELS[previewBooking.status] || previewBooking.status} />
              <Row k="Requests" v={previewBooking.special_requests || "—"} />
              {previewBooking.status === "on_hold" && previewBooking.hold_expires_at && (
                <Row k="Hold Expires" v={`${formatPHTDate(previewBooking.hold_expires_at.slice(0, 10))} · ${holdExpiryLabel(previewBooking.hold_expires_at)}`} />
              )}
              {previewBooking.created_at ? <Row k="Created" v={formatPHTDateTime(previewBooking.created_at)} /> : null}
              {previewBooking.edited_at ? (
                <Row k="Last Edited" v={`${formatPHTDateTime(previewBooking.edited_at)}${previewBooking.edited_by ? " by " + previewBooking.edited_by : ""}`} />
              ) : null}
              {proofBlobUrl && (
                <div className="pt-2">
                  <p className="overline mb-2">Payment Proof</p>
                  <img src={proofBlobUrl} alt="Proof" className="rounded-lg border border-[var(--border)] max-h-96 mx-auto" />
                </div>
              )}
              <div className="pt-3 flex justify-end gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-transparent border-[var(--border)] text-[#A1A4B5] hover:text-white"
                  onClick={() => setPreviewBooking(null)}
                  data-testid="preview-close-button"
                >
                  Close
                </Button>
                {previewBooking.approval_token && (
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    onClick={async () => {
                      try {
                        await api.post(`/admin/bookings/${previewBooking.id}/resend-approval`);
                        toast.success("Approval email re-sent");
                      } catch {
                        toast.error("Failed to resend email");
                      }
                    }}
                    data-testid="resend-approval-email"
                    title="Re-send the email with one-click Confirm / Reject buttons"
                  >
                    Resend Approval Email
                  </Button>
                )}
                <Button type="button" className="gold-btn rounded-full" onClick={() => { setPreviewBooking(null); setEditBooking(previewBooking); }} data-testid="edit-from-preview">
                  <Pencil size={14} className="mr-1" /> Edit Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdminEditBookingDialog
        booking={editBooking}
        onClose={() => setEditBooking(null)}
        onSaved={() => {
          setEditBooking(null);
          load();
        }}
      />

      <AdminNewBookingDialog
        rates={rates}
        defaultDate={newBookingDate}
        open={newBookingOpen}
        onOpenChange={setNewBookingOpen}
        hideTrigger
        onCreated={() => {
          setNewBookingOpen(false);
          load();
        }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[var(--border)] rounded-xl p-4 bg-[#13151D]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A4B5]">{label}</div>
      <div className="font-serif-display text-2xl mt-1.5">{value}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[var(--border)] last:border-0">
      <span className="text-[#A1A4B5]">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}
